import React, { useEffect, useState } from 'react';
import { Link, useLocation } from "react-router-dom";
import { Helmet } from "react-helmet";
import Confetti from 'react-confetti';
import { motion } from 'framer-motion';
import { CheckCircle, FileText, Mail } from 'lucide-react';
import { trackEvent } from '../lib/analytics';

function TransactionSuccessPage() {
  const [showConfetti, setShowConfetti] = useState(true);
  const [currentStep, setCurrentStep] = useState(0);
  const [loadingMessage, setLoadingMessage] = useState(0);

  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const totalAmount = searchParams.get('amount') || 0;
  const services = searchParams.get('services')
    ? JSON.parse(decodeURIComponent(searchParams.get('services')))
    : {};

  const loadingMessages = [
    "Preparing your files with extra care",
    "Adding a sprinkle of magic",
    "Teaching the robots to dance",
    "Almost there! Just dotting the i's"
  ];

  useEffect(() => {
    const messageInterval = setInterval(() => {
      setLoadingMessage((prev) => (prev + 1) % loadingMessages.length);
    }, 3000);

    const stepInterval = setInterval(() => {
      setCurrentStep((prev) => Math.min(prev + 1, 2));
    }, 2000);

    const confettiTimeout = setTimeout(() => {
      setShowConfetti(false);
    }, 5000);

    // Only track purchase success if we have valid transaction data
    // and this is the first time tracking this specific transaction
    if (totalAmount && Object.keys(services).length > 0) {
      // Use sessionStorage to prevent duplicate tracking on page refresh
      const transactionId = searchParams.get('transaction_id');
      const hasTracked = sessionStorage.getItem(`tracked_purchase_${transactionId}`);
      
      if (transactionId && !hasTracked) {
        trackEvent("purchase.completed", {
          total_amount: parseFloat(totalAmount),
          services_purchased: services,
          page: "success",
          transaction_id: transactionId
        });
        
        // Mark this transaction as tracked
        sessionStorage.setItem(`tracked_purchase_${transactionId}`, 'true');
      }
    }

    return () => {
      clearInterval(messageInterval);
      clearInterval(stepInterval);
      clearTimeout(confettiTimeout);
    };
  }, [totalAmount, services, loadingMessages.length]);

  const steps = [
    { icon: CheckCircle, text: "Payment Processed", color: "text-green-500" },
    { icon: FileText, text: "Processing File", color: "text-blue-500" },
    { icon: Mail, text: "Email Delivery", color: "text-purple-500" }
  ];

  return (
    <div>
      <Helmet>
        <script>
          {`
            gtag('event', 'conversion', {
              'send_to': 'AW-877929943/THVdCOya5fAYENfL0KID',
              'value': 1.0,
              'currency': 'USD'
            });
          `}
        </script>
      </Helmet>
      <div className="bg-gradient-to-br from-purple-50 to-blue-50 min-h-screen flex items-center justify-center p-4">
        {showConfetti && <Confetti recycle={false} numberOfPieces={200} />}
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          className="bg-white p-8 rounded-2xl shadow-lg max-w-md w-full"
        >
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: "spring", stiffness: 200, damping: 15 }}
            className="w-20 h-20 mx-auto mb-6"
          >
            <CheckCircle className="w-full h-full text-green-500" />
          </motion.div>

          <motion.h1
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            className="text-3xl font-bold mb-4 text-center bg-clip-text text-transparent bg-gradient-to-r from-purple-600 to-blue-600"
          >
            Woohoo! Success!
          </motion.h1>

          <div className="space-y-6 mb-8">
            <div className="space-y-4">
              {steps.map((step, index) => (
                <motion.div
                  key={index}
                  initial={{ x: -20, opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                  transition={{ delay: index * 0.2 }}
                  className={`flex items-center space-x-3 ${index <= currentStep ? 'opacity-100' : 'opacity-50'}`}
                >
                  <step.icon className={`w-6 h-6 ${step.color}`} />
                  <div className="flex-1">
                    <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                      <motion.div
                        initial={{ width: "0%" }}
                        animate={{ width: index <= currentStep ? "100%" : "0%" }}
                        transition={{ duration: 1 }}
                        className={`h-full ${index <= currentStep ? 'bg-gradient-to-r from-purple-500 to-blue-500' : 'bg-gray-200'}`}
                      />
                    </div>
                  </div>
                  <span className={`text-sm font-medium ${index <= currentStep ? step.color : 'text-gray-400'}`}>
                    {step.text}
                  </span>
                </motion.div>
              ))}
            </div>

            <motion.div
              animate={{ opacity: [0.5, 1, 0.5] }}
              transition={{ duration: 2, repeat: Infinity }}
              className="text-center text-gray-600"
            >
              {loadingMessages[loadingMessage]}
            </motion.div>
          </div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5, duration: 1 }}
            className="text-center text-sm text-gray-600 mb-4"
          >
            <p>
              Your files should be processed and delivered within the next <strong>2 hours</strong>. If you don't see the email, please check your spam folder.
            </p>
          </motion.div>

          <motion.div
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <Link
              to="/"
              className="block w-full bg-gradient-to-r from-purple-500 to-blue-500 hover:from-purple-600 hover:to-blue-600 text-white font-bold py-3 px-4 rounded-lg text-center transition-all duration-200 shadow-md hover:shadow-lg"
            >
              Return to Main Page
            </Link>
          </motion.div>
        </motion.div>
      </div>
    </div>
  );
}

export default TransactionSuccessPage;
